import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { useQuery } from '@apollo/client';
import { GetFullProfileDocument } from '@app/api/generated/graphql';
import { useAuthStore } from '@app/store/auth.store';
import { useUiStore } from '@app/store/ui.store';
import { mediaUrl } from '@app/lib/cdn';
import { AuthModal } from '@app/features/auth/AuthModal';
import { Icon } from '@landing/icons/Icons';
import { CTA, HEADER } from './content';
import { u } from '@landing/base';

/** Значок платформы. Разделитель-точка больше не нужен — его роль взял значок. */
const PLATFORM_ICON = { iPhone: 'apple', Android: 'android', Web: 'globe' };

/*
 * Шапка, бегущая строка и призыв к установке — детали, общие для всех
 * вариантов секций. Живут отдельно, чтобы варианты отличались композицией,
 * а не своими копиями одних и тех же элементов.
 */

/**
 * Шапка: знак слева, разделы по центру, вход справа.
 *
 * Справа не призыв к установке, а вход в веб-версию: это другое действие.
 * Кнопка установки живёт на первом экране и в блоке 5, и дублировать её
 * здесь значило бы заставлять выбирать между двумя одинаковыми кнопками.
 *
 * Выбора языка в шапке нет: пока готов один язык, список из одного пункта
 * не даёт выбора, а место занимает.
 *
 * `bare` — для страницы установки. Там нет ни одной из секций лендинга, и
 * якоря вели бы в пустоту; вход в веб-версию тоже неуместен — эта страница
 * решает ровно одну задачу, поставить приложение на Android, и Web в ней
 * не предлагается. Остаётся только знак, и он ведёт на лендинг.
 */
export function Header({ bare = false }) {
  const [frosted, setFrosted] = useState(false);
  const [active, setActive] = useState(null);
  const navigate = useNavigate();
  const openAuthModal = useUiStore((s) => s.openAuthModal);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);

  // Профиль запрашиваем только вошедшим; ошибку глотаем — из-за неё шапка
  // рушиться не должна.
  const { data } = useQuery(GetFullProfileDocument, {
    skip: !isAuthenticated,
    errorPolicy: 'ignore',
  });
  const user = data?.getUser;
  const userName = user?.nickname || user?.firstname || 'Профиль';
  const avatar = mediaUrl(user?.avatar?.path);

  // Один порог, поэтому обработчик скролла здесь дешевле наблюдателя.
  useEffect(() => {
    const onScroll = () => setFrosted(window.scrollY > 16);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  /*
   * Текущий раздел. Наблюдатель, а не обработчик скролла: он будит нас
   * только на границах секций, тогда как scroll срабатывает на каждый кадр.
   *
   * Полоса сужена до середины экрана — активным считается тот раздел, что
   * сейчас в центре взгляда. Иначе на границе двух секций подсветка прыгает
   * туда-сюда, потому что видны обе.
   *
   * На первом экране не подсвечено ничего: hero не пункт меню, и назначать
   * ему чужой раздел значило бы врать.
   */
  useEffect(() => {
    if (bare) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const seen = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
        if (seen) setActive(seen.target.id);
      },
      { rootMargin: '-45% 0px -45% 0px', threshold: [0, 0.2, 0.6, 1] }
    );

    for (const { id } of HEADER.nav) {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    }

    /*
     * Последний раздел — особый случай. Футер невысокий и до середины экрана
     * не доходит никогда: страница заканчивается раньше. С одной только
     * полосой по центру «Контакты» не подсветились бы ни разу.
     *
     * Поэтому у конца страницы просто назначаем последний пункт. Сравнение
     * двух чисел — обработчик скролла здесь дешевле ещё одного наблюдателя.
     */
    const last = HEADER.nav[HEADER.nav.length - 1].id;
    const onScroll = () => {
      const atBottom =
        window.scrollY + window.innerHeight >=
        document.documentElement.scrollHeight - 4;
      if (atBottom) setActive(last);
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });

    return () => {
      observer.disconnect();
      window.removeEventListener('scroll', onScroll);
    };
  }, [bare]);

  if (bare) {
    return (
      <header className={`v4__header ${frosted ? 'v4__header--frosted' : ''}`}>
        <div className="v4__headerInner v4__headerInner--bare">
          <a className="v4__mark" href={u('/')} aria-label="AURA — на главную">
            <img src={u('/aura-logo.png')} alt="" />
          </a>
        </div>
      </header>
    );
  }

  return (
    <>
      <header className={`v4__header ${frosted ? 'v4__header--frosted' : ''}`}>
        <div className="v4__headerInner">
          {/* Знак слева. Только иконка, без слова — ведёт наверх. */}
          <a className="v4__mark" href="#top" aria-label="AURA — наверх">
            <img src={u('/aura-logo.png')} alt="" />
          </a>

          <nav className="v4__nav" aria-label="Разделы страницы">
            {HEADER.nav.map((n) => (
              <a
                key={n.id}
                href={`#${n.id}`}
                className={active === n.id ? 'is-active' : ''}
                aria-current={active === n.id ? 'true' : undefined}
                /*
                 * Подпись дублируется в data-атрибут: из неё CSS делает
                 * невидимую жирную копию и по ней задаёт ширину пункта.
                 * Без этого ряд дёргался бы при каждой смене раздела —
                 * жирное начертание шире обычного.
                 */
                data-label={n.label}
              >
                {n.label}
              </a>
            ))}
          </nav>

          {isAuthenticated ? (
            <button
              type="button"
              className="v4__account"
              onClick={() => navigate('/app/feed')}
              aria-label={`Открыть AURA как ${userName}`}
            >
              {avatar ? (
                <img className="v4__avatar" src={avatar} alt="" />
              ) : (
                <span className="v4__avatar">{userName.charAt(0).toUpperCase()}</span>
              )}
              <span className="v4__accountName">{userName}</span>
            </button>
          ) : (
            <button
              type="button"
              className="v4__signin"
              onClick={() => openAuthModal({ title: 'Вход в AURA' })}
            >
              Войти
            </button>
          )}
        </div>
      </header>

      <AuthModal />
    </>
  );
}

/** Кнопка установки и строка платформ под ней. Кнопка везде чёрная. */
export function InstallCta({ onClick, className = '', size }) {
  return (
    <div className={`v4__ctaBlock ${className}`}>
      <button
        type="button"
        className={`v4__cta ${size === 'sm' ? 'v4__cta--sm' : ''}`}
        onClick={onClick}
      >
        {CTA.install}
      </button>
      <p className="v4__platforms">
        {CTA.platforms.map((p) => (
          <span key={p}>
            <Icon name={PLATFORM_ICON[p]} />
            {p}
          </span>
        ))}
      </p>
    </div>
  );
}

/**
 * Бегущая строка. Содержимое дублируется ровно вдвое: анимация сдвигает
 * дорожку на половину её ширины, и склейка приходится на одинаковый кадр —
 * иначе на стыке виден рывок.
 */
export function Marquee({ text, thin = false, tone = 'plain' }) {
  // Разделитель уже стоит внутри строки варианта — здесь только повтор.
  const items = Array.from({ length: 8 }, () => `${text} · `);
  return (
    <div
      className={`v4__marquee ${thin ? 'v4__marquee--thin' : ''} v4__marquee--${tone}`}
      aria-hidden="true"
    >
      <div className="v4__marqueeTrack">
        {items.map((t, i) => (
          <span key={i}>{t}</span>
        ))}
      </div>
    </div>
  );
}
